import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ChevronDown, 
  Instagram, 
  Twitter, 
  Youtube, 
  MessageSquare,
  Grid,
  Activity,
  Users,
  Trophy,
  Map,
  Calendar,
  Flame,
  FileText,
  Code,
  Zap,
  Coins,
  Facebook,
  Send,
  Music,
  Menu,
  X,
  Shield,
  Handshake,
  Building2,
  Heart,
  Film
} from 'lucide-react';
import { ThirdwebClient } from 'thirdweb';
import { ConnectButton, useActiveAccount } from 'thirdweb/react';
import { createWallet, inAppWallet } from 'thirdweb/wallets';
import MobileMenu from './MobileMenu';
import UserDropdown from './UserDropdown';

interface HeaderProps {
  currentPage: string;
  onPageChange: (page: string) => void;
  client: ThirdwebClient;
}

const socials = [
  { name: 'Telegram', url: 'https://t.me/ScavenjerGC', icon: Send },
  { name: 'Twitter', url: 'https://x.com/ScavenjerIO', icon: Twitter },
  { name: 'Instagram', url: 'https://www.instagram.com/scavenjer/', icon: Instagram },
  { name: 'YouTube', url: 'https://www.youtube.com/@ScavenjerOfficial/', icon: Youtube },
  { name: 'Facebook', url: 'https://www.facebook.com/Scavenjer/', icon: Facebook },
  { name: 'Discord', url: 'https://discord.gg/RQXjBJKJaw', icon: MessageSquare }
];

export default function Header({ currentPage, client, onPageChange }: HeaderProps) {
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const account = useActiveAccount();

  // Configure in-app wallet (email, phone, social) and external wallets
  const wallets = [
    inAppWallet({
      auth: {
        options: ["email", "phone", "google", "apple", "facebook", "passkey"],
      },
    }),
    createWallet("io.metamask"),
    createWallet("com.coinbase.wallet"),
    createWallet("me.rainbow"),
    createWallet("walletConnect"),
    createWallet("com.trustwallet.app"),
    createWallet("com.okex.wallet"),
    createWallet("com.brave.wallet"),
    createWallet("com.ledger"),
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const isCurrentPath = (path: string) => location.pathname === path;

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#111111]/95 backdrop-blur-sm border-b border-[#2DD4BF]/20">
        <div className="container mx-auto">
          <div className="flex items-center justify-between h-[72px] px-4 md:px-6">
            {/* Logo */}
            <button 
              onClick={() => handleNavigation('/')}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <img
                src="https://ik.imagekit.io/q9x52ygvo/Untitled.png?updatedAt=1731900408675"
                alt="Scavenjer Logo"
                className="w-16 h-16 md:w-14 md:h-14 object-contain"
              />
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <button
                onClick={() => handleNavigation('/')}
                className={`text-white hover:text-[#2DD4BF] font-medium text-sm tracking-[0.2em] transition-colors ${
                  isCurrentPath('/') ? 'text-[#2DD4BF]' : ''
                }`}
              >
                HOME
              </button>

              {/* Planned release: Simulations nav link preserved off-menu.
              <a 
                href="https://simulations-gamma.vercel.app/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-[#2DD4BF] font-medium text-sm tracking-[0.2em] transition-colors"
              >
                SIMULATIONS
              </a>
              */}

              {/* Support Shop Link */}
              <button 
                onClick={() => handleNavigation('/support-shop')}
                className={`text-white hover:text-[#2DD4BF] font-medium text-sm tracking-[0.2em] transition-colors ${
                  isCurrentPath('/support-shop') ? 'text-[#2DD4BF]' : ''
                }`}
              >
                SUPPORT SHOP
              </button>

              {/* Socials Dropdown */}
              <div className="nav-item">
                <button className="flex items-center gap-1 text-white hover:text-[#2DD4BF] font-medium text-sm tracking-[0.2em] transition-colors">
                  SOCIALS
                  <ChevronDown size={16} />
                </button>
                <div className="dropdown-menu">
                  <div className="dropdown-section">
                    <div className="dropdown-section-title">Social Media</div>
                    <a href={socials.find(s => s.name === 'Instagram')?.url} target="_blank" rel="noopener noreferrer" className="dropdown-item">
                      <Instagram size={16} />
                      Instagram
                    </a>
                    <a href={socials.find(s => s.name === 'Twitter')?.url} target="_blank" rel="noopener noreferrer" className="dropdown-item">
                      <Twitter size={16} />
                      X.com
                    </a>
                    <a href={socials.find(s => s.name === 'Facebook')?.url} target="_blank" rel="noopener noreferrer" className="dropdown-item">
                      <Facebook size={16} />
                      Facebook
                    </a>
                    <a href={socials.find(s => s.name === 'YouTube')?.url} target="_blank" rel="noopener noreferrer" className="dropdown-item">
                      <Youtube size={16} />
                      YouTube
                    </a>
                  </div>
                  
                  <div className="dropdown-divider" />
                  
                  <div className="dropdown-section">
                    <div className="dropdown-section-title">Community</div>
                    <a href={socials.find(s => s.name === 'Discord')?.url} target="_blank" rel="noopener noreferrer" className="dropdown-item">
                      <MessageSquare size={16} />
                      Discord
                    </a>
                    <a href={socials.find(s => s.name === 'Telegram')?.url} target="_blank" rel="noopener noreferrer" className="dropdown-item">
                      <Send size={16} />
                      Telegram
                    </a>
                  </div>
                </div>
              </div>

              {/* More Dropdown */}
              <div className="nav-item">
                <button className={`flex items-center gap-1 text-white hover:text-[#2DD4BF] font-medium text-sm tracking-[0.2em] transition-colors ${
                  ['/leaderboard', '/roadmap', '/whitepaper', '/hosts', '/careers', '/business', '/studios'].includes(location.pathname) ? 'text-[#2DD4BF]' : ''
                }`}>
                  MORE
                  <ChevronDown size={16} />
                </button>
                <div className="dropdown-menu">
                  {/* Community Section */}
                  <div className="dropdown-section">
                    <div className="dropdown-section-title">Community</div>
                    <button onClick={() => handleNavigation('/hosts')} className="dropdown-item"><Users size={16} />Hosts</button>
                    <button onClick={() => handleNavigation('/leaderboard')} className="dropdown-item"><Trophy size={16} />Leaderboard</button>
                    <button onClick={() => handleNavigation('/careers')} className="dropdown-item"><Zap size={16} />Careers</button>
                  </div>
                  
                  <div className="dropdown-divider" />
                  
                  {/* Information Section */}
                  <div className="dropdown-section">
                    <div className="dropdown-section-title">Information</div>
                    <button onClick={() => handleNavigation('/roadmap')} className="dropdown-item"><Map size={16} />Roadmap</button>
                    <a href="https://scavenjerbusiness.com/" className="dropdown-item"><Building2 size={16} />For Businesses</a>
                  </div>
                  
                  <div className="dropdown-divider" />

                  {/* Content Section */}
                  <div className="dropdown-section">
                    <div className="dropdown-section-title">Content</div>
                    <button onClick={() => handleNavigation('/studios')} className="dropdown-item"><Film size={16} />Studios</button>
                  </div>

                  <div className="dropdown-divider" />

                  {/* Documentation Section */}
                  <div className="dropdown-section">
                    <div className="dropdown-section-title">Documentation</div>
                    <button onClick={() => handleNavigation('/whitepaper')} className="dropdown-item"><FileText size={16} />Whitepaper</button>
                  </div>

                  <div className="dropdown-divider" />

                  {/* Legal Section */}
                  <div className="dropdown-section">
                    <div className="dropdown-section-title">Legal</div>
                    <button onClick={() => handleNavigation('/terms')} className="dropdown-item"><FileText size={16} />Terms & Conditions</button>
                    <button onClick={() => handleNavigation('/privacy')} className="dropdown-item"><Shield size={16} />Privacy Policy</button>
                  </div>
                </div>
              </div>
            </nav>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center">
              {account ? (
                <UserDropdown client={client} />
              ) : (
                <ConnectButton 
                  client={client} 
                  wallets={wallets}
                  connectModal={{
                    size: "compact",
                    showThirdwebBranding: false,
                    welcomeScreen: {
                      title: "Connect your account",
                      subtitle: "Sign in with email, phone, social login, or connect an external wallet",
                    }
                  }}
                />
              )}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                className="ml-4 text-white"
              >
                {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
            
            {/* Wallet Connect Button / User Dropdown for Desktop */}
            <div className="hidden md:flex items-center">
              {account ? (
                <UserDropdown client={client} />
              ) : (
                <ConnectButton 
                  client={client} 
                  wallets={wallets}
                  connectModal={{
                    size: "compact",
                    showThirdwebBranding: false,
                    welcomeScreen: {
                      title: "Connect your account",
                      subtitle: "Sign in with email, phone, social login, or connect an external wallet",
                    }
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </header>
      
      <MobileMenu 
        isOpen={isMobileMenuOpen} 
        onClose={() => setIsMobileMenuOpen(false)} 
        navigate={handleNavigation}
        isCurrentPath={isCurrentPath}
        client={client}
      />
    </>
  );
}
